import { useState, type FormEvent } from 'react'
import { Table, useApiQuery, useToast, type Column } from 'grabbitbot-legacy-ui'
import { screenerClient } from '../../api'
import type { TextData } from '../../api/types'

const EMPTY: Partial<TextData> = { name: '', content: '', description: '' }

/** Equivalent of legacy `TextdataListComponent` — CRUD for named text
 * snippets (audit §6: "likely used as reusable templates ... for webhook
 * payload bodies or similar"). List + inline create/edit form, one shared
 * `Table` for the list per fleet frontend-standards §1. */
export function TextdataPage() {
  const { data, loading, error, refetch } = useApiQuery(() => screenerClient.searchTextdata(), [])
  const { showToast } = useToast()
  const [editing, setEditing] = useState<Partial<TextData> | null>(null)
  const [saving, setSaving] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!editing) return
    setSaving(true)
    try {
      if (editing.id) {
        await screenerClient.modifyTextdata(editing.id, editing)
      } else {
        await screenerClient.createTextdata(editing)
      }
      setEditing(null)
      refetch()
      showToast('Saved', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not save', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function remove(id: string) {
    await screenerClient.deleteTextdata(id)
    refetch()
  }

  const columns: Column<TextData>[] = [
    { key: 'name', label: 'Name', align: 'left' },
    { key: 'description', label: 'Description', align: 'left' },
    {
      key: 'id',
      label: '',
      sortable: false,
      format: (_v, row) => (
        <span className="screener-actions">
          <button type="button" onClick={() => setEditing(row)}>
            Edit
          </button>
          <button type="button" onClick={() => row.id && remove(row.id)}>
            Delete
          </button>
        </span>
      ),
    },
  ]

  return (
    <div>
      <div className="screener-page-title">
        <h1>Text data</h1>
        <button type="button" onClick={() => setEditing({ ...EMPTY })}>
          New snippet
        </button>
      </div>

      {editing && (
        <form className="screener-form screener-card" onSubmit={handleSubmit}>
          <label>
            Name
            <input value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required />
          </label>
          <label>
            Description
            <input
              value={editing.description ?? ''}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            />
          </label>
          <label>
            Content
            <textarea
              rows={6}
              value={editing.content ?? ''}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
            />
          </label>
          <div className="screener-actions">
            <button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <p>Loading…</p>}
      {error && <p role="alert">Could not load text data.</p>}
      {data && <Table columns={columns} rows={data} rowKey={(row, i) => row.id ?? i} initialSort={{ key: 'name', dir: 'asc' }} />}
    </div>
  )
}
